import { ATTRIBUTES, ATTRIBUTE_IDS, qualityById } from "@personnel/rules-leviathan";
import type { LeviathanCaptain, LeviathanDerived } from "@personnel/rules-leviathan";
import { rankTitle } from "@personnel/setting";

/**
 * Captain card front, after FASA's printed cards (sample 62002-01).
 *
 * Authored as SVG rather than HTML so the PNG export is pixel-identical to the
 * preview: the same markup is serialized and rasterized, with no DOM-to-image
 * approximation in between.
 *
 * The Stress track and the Fleet/Command boxes are deliberately static. Their
 * colours, numbers and letters are fixed artwork marked during play, not values
 * derived from the captain.
 */

export const CARD_WIDTH = 750;
export const CARD_HEIGHT = 1050;

/** Fleet and Command boxes on the Assignment row. Marked during play. */
const ASSIGNMENT_BOXES = [
  { letter: "F", x: 46 },
  { letter: "C", x: 100 },
];

const TRACK = {
  /** Squares per tier, top to bottom. */
  tiers: [3, 3, 2, 2],
  colours: ["#0F9D58", "#C4D82E", "#F2A03D", "#C8283C"],
  morale: ["1", "2", "3", "4"],
  command: ["4", "3", "2", "1"],
};

interface CaptainCardProps {
  captain: LeviathanCaptain;
  derived: LeviathanDerived;
  ref?: React.Ref<SVGSVGElement>;
}

function trackSquares() {
  const top = 200;
  const size = 46;
  const gap = 12;
  const tierGap = 16;

  const squares: { x: number; y: number; fill: string }[] = [];
  const tierBounds: { top: number; bottom: number; index: number }[] = [];

  let y = top;
  TRACK.tiers.forEach((count, tierIndex) => {
    const tierTop = y;
    for (let i = 0; i < count; i++) {
      squares.push({ x: 514, y, fill: TRACK.colours[tierIndex] ?? "#888" });
      y += size + gap;
    }
    tierBounds.push({ top: tierTop, bottom: y - gap, index: tierIndex });
    y += tierGap;
  });

  return { squares, tierBounds, size };
}

export function CaptainCard({ captain, derived, ref }: CaptainCardProps) {
  const { squares, tierBounds, size } = trackSquares();

  const qualityNames = captain.qualities
    .map((id) => qualityById(id)?.name)
    .filter((name): name is string => name !== undefined);
  const qualityLine = qualityNames.length === 0 ? "None" : qualityNames.join(" · ");

  // The line above the name is the faction rank title, following the effective
  // Rank so Distinguished Career promotes the printed title too. Falls back to
  // the generic "Captain" when no faction has been chosen.
  const title = rankTitle(captain.faction, derived.effectiveRank) ?? "Captain";

  // Shrink the Qualities line as it grows so three Quality names still fit.
  const qualityFontSize = qualityLine.length > 52 ? 20 : qualityLine.length > 34 ? 26 : 34;

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      role="img"
      aria-label={`Captain card for ${captain.name || "unnamed captain"}`}
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id="cardEdge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0d1b2a" />
          <stop offset="45%" stopColor="#1b3a5c" />
          <stop offset="100%" stopColor="#3d1f14" />
        </linearGradient>
      </defs>

      <rect width={CARD_WIDTH} height={CARD_HEIGHT} fill="url(#cardEdge)" />

      {/* --- Header: name and Cost --- */}
      <g>
        <rect x="30" y="34" width="516" height="116" rx="4" fill="#fff" stroke="#111" strokeWidth="3" />
        <text x="288" y="78" textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="34" fill="#111">
          {title}
        </text>
        <text
          x="288"
          y="124"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize={captain.name.length > 22 ? 30 : 40}
          fill="#111"
        >
          {captain.name || "—"}
        </text>
        <text x="536" y="142" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="17" fill="#111">
          NAME
        </text>

        <rect x="556" y="34" width="164" height="116" rx="4" fill="#fff" stroke="#111" strokeWidth="3" />
        <text
          x="638"
          y="112"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize="62"
          fill="#111"
        >
          {captain.showRoundedCost ? derived.roundedCost : derived.totalCost}
        </text>
        <text x="710" y="142" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="17" fill="#111">
          COST
        </text>
      </g>

      {/* --- Ratings --- */}
      <g>
        <rect x="30" y="162" width="384" height="684" rx="4" fill="#E4E4E4" stroke="#111" strokeWidth="3" />
        {ATTRIBUTE_IDS.map((id, index) => {
          const cy = 226 + index * 104;
          const rating = id === "rank" ? derived.effectiveRank : captain.attributes[id];
          return (
            <g key={id}>
              <circle cx="112" cy={cy} r="42" fill="#fff" stroke="#111" strokeWidth="5" />
              <text
                x="112"
                y={cy + 17}
                textAnchor="middle"
                fontFamily="system-ui, sans-serif"
                fontSize="48"
                fill="#111"
              >
                {rating}
              </text>
              <text x="176" y={cy + 14} fontFamily="system-ui, sans-serif" fontSize="34" fill="#111">
                {ATTRIBUTES[id].name.toUpperCase()}
              </text>
            </g>
          );
        })}
        <text x="404" y="828" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="26" fill="#111">
          RATINGS
        </text>
      </g>

      {/* --- Stress track (static artwork) --- */}
      <g>
        <rect x="424" y="162" width="296" height="684" rx="4" fill="#E4E4E4" stroke="#111" strokeWidth="3" />

        {squares.map((square, i) => (
          <rect
            key={i}
            x={square.x}
            y={square.y}
            width={size}
            height={size}
            fill={square.fill}
            stroke="#111"
            strokeWidth="2.5"
          />
        ))}

        {tierBounds.map(({ top, bottom, index }) => {
          // `bottom` is already the lower edge of the last square in the tier,
          // so the centre is a plain midpoint -- adding `size` here pushed the
          // numbers and checkboxes a full half-square low.
          const mid = (top + bottom) / 2;
          return (
            <g key={index}>
              <path
                d={`M509 ${top - 6} h-9 v${bottom - top + 12} h9`}
                fill="none"
                stroke="#111"
                strokeWidth="3"
              />
              <path
                d={`M565 ${top - 6} h9 v${bottom - top + 12} h-9`}
                fill="none"
                stroke="#111"
                strokeWidth="3"
              />
              <text x="480" y={mid + 10} textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="30" fill="#111">
                {TRACK.morale[index]}
              </text>
              <text x="596" y={mid + 10} textAnchor="middle" fontFamily="system-ui, sans-serif" fontSize="30" fill="#111">
                {TRACK.command[index]}
              </text>
              <rect x="650" y={mid - 22} width="44" height="44" fill="#fff" stroke="#111" strokeWidth="3" />
            </g>
          );
        })}

        <text
          x="450"
          y="504"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize="20"
          fill="#C8283C"
          transform="rotate(-90 450 504)"
          letterSpacing="4"
        >
          MORALE
        </text>
        <text
          x="624"
          y="504"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize="20"
          fill="#2C7FB8"
          transform="rotate(-90 624 504)"
          letterSpacing="4"
        >
          COMMAND
        </text>

        <text x="710" y="828" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="26" fill="#111">
          STRESS
        </text>
      </g>

      {/* --- Assignment --- */}
      <g>
        <rect x="30" y="858" width="690" height="74" rx="4" fill="#fff" stroke="#111" strokeWidth="3" />
        {/* Fleet and Command designations, marked during play. */}
        {ASSIGNMENT_BOXES.map(({ letter, x }) => (
          <g key={letter}>
            <rect x={x} y="873" width="44" height="44" fill="#fff" stroke="#111" strokeWidth="3" />
            <text
              x={x + 22}
              y="906"
              textAnchor="middle"
              fontFamily="system-ui, sans-serif"
              fontSize="30"
              fill="#111"
            >
              {letter}
            </text>
          </g>
        ))}
        <text x="710" y="922" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="19" fill="#111">
          ASSIGNMENT
        </text>
      </g>

      {/* --- Qualities --- */}
      <g>
        <rect x="30" y="940" width="690" height="74" rx="4" fill="#fff" stroke="#111" strokeWidth="3" />
        <text
          x="375"
          y="988"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize={qualityFontSize}
          fill="#111"
        >
          {qualityLine}
        </text>
        <text x="710" y="1004" textAnchor="end" fontFamily="system-ui, sans-serif" fontSize="19" fill="#111">
          QUALITIES
        </text>
      </g>
    </svg>
  );
}
